import { useId } from "react";
import type { PrescriptionAuthorizationPolicy } from "@/types/clinician";

export function PrescriptionAuthorization({
  policy,
  refills,
  confirmed,
  onRefills,
  onConfirm,
  disabled = false,
}: {
  policy: PrescriptionAuthorizationPolicy;
  refills: number;
  confirmed: boolean;
  onRefills: (refills: number) => void;
  onConfirm: (confirmed: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  // days_per_fill includes the backend's authoritative container quantity.
  const totalDays =
    refills === policy.refills
      ? policy.total_days
      : policy.days_per_fill * (refills + 1);
  return (
    <section
      className="border border-warning/30 bg-warning-wash p-12 flex flex-col gap-8"
      aria-label="Prescription authorization"
    >
      <h3 className="type-label-sm">
        Prescription authorization · {policy.label}
      </h3>
      <p className="type-body-sm">
        Initial fill + {refills} additional refill{refills === 1 ? "" : "s"} ·{" "}
        {policy.days_per_fill} days per full shipment · {totalDays} total
        authorized days.
      </p>
      <p className="text-11 text-ink-secondary">
        Clinical review interval: {policy.review_interval_days} days. This
        authorization is separate from the patient’s subscription or billing
        schedule. Fewer refills may be authorized; the policy maximum cannot be
        exceeded.
      </p>
      <label htmlFor={`${id}-refills`} className="type-label-xs">
        Additional refills
      </label>
      <select
        id={`${id}-refills`}
        value={refills}
        disabled={disabled}
        onChange={(event) => onRefills(Number(event.target.value))}
        className="border border-line bg-page px-10 py-8 type-body-sm"
      >
        {Array.from({ length: policy.refills + 1 }, (_, value) => (
          <option key={value} value={value}>
            {value}
            {value === 0
              ? " — initial fill only"
              : value === policy.refills
                ? " — policy maximum"
                : ""}
          </option>
        ))}
      </select>
      <label className="flex gap-8 items-start type-body-sm">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={disabled}
          onChange={(event) => onConfirm(event.target.checked)}
          className="mt-3"
        />
        I explicitly authorize the initial fill and {refills} additional refill
        {refills === 1 ? "" : "s"} shown above after clinical review.
      </label>
      <p className="text-11 text-ink-muted">Policy {policy.policy_version}</p>
    </section>
  );
}
